import { useMemo, useState } from "react";
import { AlertTriangle } from "lucide-react";
import type { HydratedQuoteResponse } from "@/hooks/useDirectQuoteRequests";
import ShipperQuoteResponseCard, { type QuoteLabel } from "./ShipperQuoteResponseCard";
import { cn } from "@/lib/utils";

type SortKey = "recommended" | "lowest" | "fastest" | "recent";

const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: "recommended", label: "Recommended" },
  { key: "lowest", label: "Lowest Price" },
  { key: "fastest", label: "Fastest Response" },
  { key: "recent", label: "Recently Submitted" },
];

const fmtMoney = (n: number | null | undefined) =>
  n == null ? "—" : `$${Math.round(Number(n)).toLocaleString()}`;

export interface ShipperQuoteResponsesListProps {
  responses: HydratedQuoteResponse[];
  parentCreatedAt: string;
  miles: number | null;
  targetRate: number | null;
  onMessageBroker: (userId: string, name: string) => void;
  onSelectResponse?: (response: HydratedQuoteResponse) => void;
  selectLabel?: string;
  selectDisabled?: boolean;
  /** Show the "quotes are indicative" callout above the list. */
  showIndicativeCallout?: boolean;
}

export default function ShipperQuoteResponsesList({
  responses,
  parentCreatedAt,
  miles,
  targetRate,
  onMessageBroker,
  onSelectResponse,
  selectLabel,
  selectDisabled,
  showIndicativeCallout = true,
}: ShipperQuoteResponsesListProps) {
  const [sort, setSort] = useState<SortKey>("recommended");

  const active = useMemo(
    () => responses.filter((r) => r.status === "active" || r.status === "awarded"),
    [responses],
  );
  const inactive = useMemo(
    () => responses.filter((r) => !(r.status === "active" || r.status === "awarded")),
    [responses],
  );

  const stats = useMemo(() => {
    const rates = active.map((r) => Number(r.rate));
    if (!rates.length) return { low: null, avg: null, high: null, spread: null };
    const low = Math.min(...rates);
    const high = Math.max(...rates);
    const avg = rates.reduce((a, b) => a + b, 0) / rates.length;
    return { low, avg, high, spread: high - low };
  }, [active]);

  // Precompute per-response signals used for both sort and labels.
  const enriched = useMemo(() => {
    // Response time in ms.
    const rtMs = (r: HydratedQuoteResponse) =>
      new Date(r.created_at).getTime() - new Date(parentCreatedAt).getTime();
    const fastestId = active.reduce<{ id: string | null; ms: number }>(
      (best, r) => {
        const ms = rtMs(r);
        return ms > 0 && ms < best.ms ? { id: r.id, ms } : best;
      },
      { id: null, ms: Infinity },
    ).id;
    const lowestId =
      active.length && stats.low != null
        ? active.find((r) => Number(r.rate) === stats.low)?.id ?? null
        : null;

    return active.map((r) => {
      const labels: QuoteLabel[] = [];
      if (r.id === lowestId) labels.push("lowest");
      if (r.id === fastestId) labels.push("fastest");
      if (r.valid_until) {
        const ms = new Date(r.valid_until).getTime() - Date.now();
        if (ms > 0 && ms < 60 * 60_000) labels.push("expiring");
      }
      return { r, labels: labels.slice(0, 2), rtMs: rtMs(r) };
    });
  }, [active, parentCreatedAt, stats.low]);

  const sorted = useMemo(() => {
    const arr = [...enriched];
    switch (sort) {
      case "lowest":
        arr.sort((a, b) => Number(a.r.rate) - Number(b.r.rate));
        break;
      case "fastest":
        arr.sort((a, b) => a.rtMs - b.rtMs);
        break;
      case "recent":
        arr.sort(
          (a, b) =>
            new Date(b.r.created_at).getTime() - new Date(a.r.created_at).getTime(),
        );
        break;
      case "recommended":
      default:
        // Recommended = lowest rate, then fastest response as tie-breaker.
        arr.sort((a, b) => {
          const d = Number(a.r.rate) - Number(b.r.rate);
          if (d !== 0) return d;
          return a.rtMs - b.rtMs;
        });
    }
    return arr;
  }, [enriched, sort]);

  const activeCount = active.length;

  return (
    <div className="space-y-4">
      {/* Indicative-quotes callout */}
      {showIndicativeCallout && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 flex gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900 leading-snug">
            <span className="font-semibold">These are indicative quotes — not booked capacity.</span>{" "}
            The broker has not locked a truck at this rate. Prices are estimates a broker
            believes they can book at. When you're ready to move the freight,{" "}
            <span className="font-semibold">convert to a live load</span> so brokers can bid
            and commit real capacity.
          </div>
        </div>
      )}

      {/* Header row: count + sort */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Quotes ({activeCount})
        </h2>
        {activeCount > 1 && (
          <div className="flex flex-wrap items-center gap-1 text-xs">
            <span className="text-muted-foreground mr-1">Sort</span>
            {SORT_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                onClick={() => setSort(opt.key)}
                className={cn(
                  "px-2.5 py-1 rounded-full border transition-colors",
                  sort === opt.key
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background text-foreground border-border hover:bg-muted",
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Summary strip */}
      {activeCount > 0 && (
        <div className="grid grid-cols-3 rounded-lg border border-border bg-muted/30 divide-x divide-border">
          <SummaryStat label="Lowest" value={fmtMoney(stats.low)} accent />
          <SummaryStat label="Average" value={fmtMoney(stats.avg)} />
          <SummaryStat
            label="Spread"
            value={stats.spread != null ? fmtMoney(stats.spread) : "—"}
          />
        </div>
      )}

      {/* Cards */}
      {responses.length === 0 ? (
        <div className="rounded-lg border border-border p-8 text-center text-sm text-muted-foreground">
          No quotes yet.
        </div>
      ) : (
        <div className="space-y-2.5">
          {sorted.map(({ r, labels }) => (
            <ShipperQuoteResponseCard
              key={r.id}
              response={r}
              parentCreatedAt={parentCreatedAt}
              miles={miles}
              stats={{ low: stats.low, avg: stats.avg }}
              targetRate={targetRate}
              labels={labels}
              onMessage={() => onMessageBroker(r.broker_user_id, r.broker_company_name ?? "Broker")}
              onSelect={onSelectResponse ? () => onSelectResponse(r) : undefined}
              selectLabel={selectLabel}
              selectDisabled={selectDisabled}
            />
          ))}
          {inactive.length > 0 && (
            <details className="pt-2">
              <summary className="text-xs text-muted-foreground cursor-pointer select-none">
                Show {inactive.length} inactive ({[...new Set(inactive.map((r) => r.status))].join(", ")})
              </summary>
              <div className="space-y-2.5 mt-2">
                {inactive.map((r) => (
                  <ShipperQuoteResponseCard
                    key={r.id}
                    response={r}
                    parentCreatedAt={parentCreatedAt}
                    miles={miles}
                    stats={{ low: stats.low, avg: stats.avg }}
                    targetRate={targetRate}
                    labels={[]}
                    onMessage={() => onMessageBroker(r.broker_user_id, r.broker_company_name ?? "Broker")}
                  />
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

function SummaryStat({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="p-3 sm:p-4">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div
        className={cn(
          "mt-1 text-lg font-semibold tabular-nums",
          accent ? "text-primary" : "text-foreground",
        )}
      >
        {value}
      </div>
    </div>
  );
}
